import React, { useEffect, useRef, useState } from "react"
import { Alert, Spinner } from "reactstrap"
import { Link } from "react-router-dom"
import { countryTimezone } from "./plannerQuery"
import { publicationEmailLine, resendable } from "./publishingLabels"
import useProposalRead from "./useProposalRead"
import { AnswerTab, HistoryTab } from "./ProposalRecord"
import PublicationResendDialog from "./PublicationResendDialog"

// A published proposal (wayfinder ticket 18 Q3; integrator default for Phase E2p): only its header — P-number · Event #, "Open event" —
// its Answer and its History; the event's live values are on the event page, and no command changes a Publicată. Its publication email
// shows how it went, and is sent again from here when it failed or may not have left (E1p pinned decision Q2).
export default function PublishedProposal({ id, organizer, settings, commands, onClose }) {
  const read = useProposalRead(id)
  const proposal = read.proposal
  const publication = proposal?.publication
  const [tab, setTab] = useState("answer")
  const [resending, setResending] = useState(false)
  const heading = useRef(null)
  useEffect(() => { heading.current?.focus() }, [])
  useEffect(() => {
    const keys = event => { if (event.key === "Escape" && !event.defaultPrevented) onClose() }
    document.addEventListener("keydown", keys)
    return () => document.removeEventListener("keydown", keys)
  }, [onClose])
  const timeZone = countryTimezone(proposal?.country) || "Europe/Bucharest"
  const title = proposal ? `${proposal.number}${publication?.eventNumber ? ` · Event #${publication.eventNumber}` : ""}` : "Published proposal"
  const email = publication?.email && proposal ? { ...publication.email, organizerId: proposal.organizer?.id } : null

  return <>
    <div className="ep-scrim" aria-hidden="true" onClick={onClose} />
    <aside role="dialog" aria-modal="true" aria-labelledby="ep-published-title" className="ep-drawer">
      <header className="ep-drawer-head">
        <div className="ep-drawer-bar">
          <span className="ep-state ep-state-published">Published</span>
          <span className="ep-spacer" />
          <button type="button" className="ep-icon-button" aria-label="Close" onClick={onClose}><i className="bx bx-x" aria-hidden="true" /></button>
        </div>
        <h2 id="ep-published-title" ref={heading} tabIndex={-1}>{title}</h2>
        {publication?.eventId && <Link className="btn btn-light btn-sm align-self-start" to={`/events/${publication.eventId}`}>Open event</Link>}
        {proposal && <span className="ep-inline">
          <span className={resendable(email) ? "ep-error-text" : "ep-meta"}>{email ? `Publication email: ${publicationEmailLine(email)}` : "No publication email was sent."}</span>
          {resendable(email) && <button type="button" className="btn btn-light btn-sm" onClick={() => setResending(true)}>Send again…</button>}
        </span>}
        <div role="tablist" aria-label="Published proposal sections" className="ep-tabs">
          {[["answer", "Answer"], ["history", "History"]].map(([key, label]) => <button type="button" role="tab" key={key} aria-selected={tab === key}
            className={"ep-tab" + (tab === key ? " is-selected" : "")} onClick={() => setTab(key)}>{label}</button>)}
        </div>
      </header>
      <div className="ep-drawer-body" role="tabpanel">
        {read.error && <Alert color="danger">{read.error}{" "}<button type="button" className="btn btn-link p-0 align-baseline" onClick={read.reload}>Retry</button></Alert>}
        {!proposal && !read.error && <div className="ep-loading"><Spinner size="sm" /> Loading the proposal…</div>}
        {proposal && tab === "answer" && <AnswerTab proposal={proposal} row={null} settings={settings} timeZone={timeZone} />}
        {proposal && tab === "history" && <HistoryTab history={read.history || []}
          context={{ timeZone, settings, organizer, competitions: proposal.competitions }} />}
      </div>
      <footer className="ep-drawer-foot">
        <span className="ep-meta">The event's values, its competitions and their fees are changed on the event page.</span>
        <span className="ep-spacer" />
        <button type="button" className="btn btn-light" onClick={onClose}>Close</button>
      </footer>
    </aside>
    {resending && <PublicationResendDialog email={email} name={organizer?.companyName || "the organizer"} commands={commands}
      onClose={() => setResending(false)} onSent={read.reload} />}
  </>
}
